import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle2, ArrowUp, ChevronDown, ExternalLink } from 'lucide-react';

/**
 * AI Visibility landing page. Brands WITHOUT a measurement project see this when they open
 * /ai-visibility from the menu (YatasAiVisibility + AiVisibilitySources, outside config/aiVisibilityBrands).
 * Page copy is English (Efe, 3 Oct 2026).
 *
 * Top of the page = LIVE quick check: the user asks one question, backend asks ChatGPT
 * (gpt-5-nano + web search, Istanbul location) and lists the brands it recommended and whether
 * this brand is among them. POST /api/ai-visibility/quick-check → GET /quick-check/:id polling
 * (~20–60 s). 3 checks per brand per day, rows in ai_visibility.quick_checks.
 * Contact form posts to the public /api/contact endpoint (mail to efe@glowytics.com).
 *
 * Design: same language as the Apple deck template (pages/_deck-sablon): white surface,
 * #f5f5f7 tiles, InterDisplay, tight large headings, single accent #0a5bd3. ONE continuous page,
 * not slide cards (Efe: "too much like a presentation"); no glow, grid or gradient text
 * (Efe: "looks obviously AI-made"). No em dashes, no emoji. Panel preview numbers are FAKE and
 * labelled "Example"; never write real brand names or unmeasured stats there.
 */

const API = 'https://api.glowytics.com';

const STEPS = [
  { title: 'Questions', text: 'Together we pick the questions shoppers in your category ask AI assistants. Brand names never appear in them.' },
  { title: 'Measurement', text: 'Every question goes to ChatGPT, Gemini and Claude with web search on, from a Turkish location.' },
  { title: 'Extraction', text: 'Every brand in each answer is captured with its rank, the reason given and the sources cited, including competitors you did not list.' },
  { title: 'Dashboard', text: 'Results open on this page. Pick a period and compare it with the previous one.' },
];

const FEATURES = [
  { title: 'Share of recommendation', text: 'How much of the recommendations in your category go to you.' },
  { title: 'Need map', text: 'Which needs you own and where a competitor gets picked instead.' },
  { title: 'Sources', text: 'The websites and pages the answers rely on.' },
  { title: 'When you are asked about', text: 'How assistants describe your brand when asked directly, and who they suggest instead.' },
  { title: 'Period comparison', text: 'Confidence intervals tell a real change apart from model noise.' },
  { title: 'Raw answers', text: 'Open the answer behind every number and read it.' },
];

const SAMPLE_SHARE = [
  { name: 'Brand A', share: 31 },
  { name: 'Your brand', share: 22, own: true },
  { name: 'Brand B', share: 17 },
  { name: 'Brand C', share: 11 },
  { name: 'Brand D', share: 7 },
];
const HEAT_BRANDS = ['You', 'A', 'B', 'C'];
const HEAT_ROWS = [
  { need: 'Value for money', v: [0.2, 0.7, 0.5, 0.3] },
  { need: 'Quality', v: [0.8, 0.6, 0.2, 0.3] },
  { need: 'Fast delivery', v: [0.1, 0.4, 0.8, 0.2] },
  { need: 'Gifting', v: [0.6, 0.3, 0.3, 0.5] },
];
const SOURCE_MIX = [
  { label: 'Brand sites', pct: 34 },
  { label: 'Marketplaces', pct: 24 },
  { label: 'Blogs & guides', pct: 22 },
  { label: 'Forums & reviews', pct: 12 },
  { label: 'News', pct: 8 },
];

const CSS = `
@font-face{font-family:'InterDisplay';src:url('/fonts/InterDisplay-Medium.woff2') format('woff2');font-weight:500;font-display:swap}
@font-face{font-family:'InterDisplay';src:url('/fonts/InterDisplay-SemiBold.woff2') format('woff2');font-weight:600;font-display:swap}
.aiv{--tile:#f5f5f7;--ink:#1d1d1f;--ink-2:#6e6e73;--ink-3:#86868b;--hair:#e0e0e3;--brand:#0a5bd3;--btn:#0071e3;
  color:var(--ink);font-family:'InterDisplay',-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',Roboto,sans-serif;
  font-weight:500;-webkit-font-smoothing:antialiased;font-variant-numeric:tabular-nums;letter-spacing:-.012em}
.aiv b{font-weight:600}
.aiv-page{background:#fff;border-radius:22px;box-shadow:0 1px 2px rgba(0,0,0,.04),0 8px 24px rgba(0,0,0,.04);overflow:hidden}
.aiv-sec{padding:88px 64px;max-width:1080px;margin:0 auto}
.aiv-sec+.aiv-sec{border-top:1px solid #ededf0}
.aiv-center{text-align:center}.aiv-center .aiv-lead{margin-left:auto;margin-right:auto}
.aiv-eyebrow{font-size:13px;font-weight:600;color:var(--brand);margin-bottom:10px}
.aiv-h1{font-size:64px;line-height:1.05;font-weight:600;letter-spacing:-.03em}
.aiv-h2{font-size:42px;line-height:1.07;font-weight:600;letter-spacing:-.028em}
.aiv-lead{margin-top:14px;font-size:18px;line-height:1.45;color:var(--ink-2);max-width:56ch;letter-spacing:-.01em}
.aiv-lead b{color:var(--ink)}
.aiv-btn{display:inline-flex;align-items:center;gap:8px;background:var(--btn);color:#fff;border-radius:980px;padding:11px 22px;font-size:15px;font-weight:500;letter-spacing:-.01em;transition:background .2s}
.aiv-btn:hover{background:#0077ed} .aiv-btn:disabled{opacity:.6}
.aiv-link{font-size:15px;color:var(--btn);letter-spacing:-.01em}
.aiv-link:hover{text-decoration:underline}
.aiv-grid{display:grid;gap:14px}
.aiv-tile{background:var(--tile);border-radius:22px;padding:24px 26px;display:flex;flex-direction:column;min-width:0;position:relative;overflow:hidden}
.aiv-tile.dark{background:#1d1d1f;color:#f5f5f7}
.aiv-tile.tint{background:linear-gradient(150deg,#e9f1fd 0%,#d4e3fb 100%)}
.aiv-lbl{font-size:12px;font-weight:600;color:var(--ink-3);margin-bottom:14px;display:flex;justify-content:space-between;gap:8px}
.aiv-tile.dark .aiv-lbl{color:#9a9aa0}
.aiv-num{font-size:60px;line-height:1;font-weight:600;letter-spacing:-.036em;margin-top:auto}
.aiv-num.sm{font-size:44px}
.aiv-cap{margin-top:8px;font-size:13.5px;line-height:1.4;color:var(--ink-2);letter-spacing:-.006em}
.aiv-tile.dark .aiv-cap{color:#a1a1a6}
.aiv-ttl{font-size:20px;font-weight:600;letter-spacing:-.022em;line-height:1.2}
.aiv-txt{margin-top:7px;font-size:14px;line-height:1.45;color:var(--ink-2);letter-spacing:-.006em}
.aiv-stepno{font-size:13px;font-weight:600;color:var(--brand);margin-bottom:12px}
.aiv-fact{padding:0 24px;border-left:1px solid var(--hair)}.aiv-fact:first-child{border-left:0;padding-left:0}
.aiv-src{font-size:11.5px;color:var(--ink-3);display:flex;flex-wrap:wrap;align-items:center;gap:6px}
.aiv-src i{font-style:normal;background:#fff;border:1px solid var(--hair);border-radius:6px;padding:1px 6px;color:var(--ink-2)}
.aiv-row{display:flex;align-items:baseline;gap:10px;background:#fff;border-radius:12px;padding:9px 12px;font-size:14px;border:1px solid transparent}
.aiv-row.own{border-color:var(--brand);box-shadow:0 0 0 3px rgba(10,91,211,.08)}
.aiv-row .k{font-size:12px;color:var(--ink-3);font-weight:600}
.aiv-row .w{font-size:12.5px;color:var(--ink-2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.aiv-in{animation:aiv-in .4s ease-out both}
@keyframes aiv-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
.aiv-bar{height:6px;border-radius:3px;background:#e3e3e8;overflow:hidden}
.aiv-bar>div{height:100%;border-radius:3px;background:#b9b9c0}
.aiv-bar>div.own{background:var(--brand)}
.aiv-feat{display:grid;grid-template-columns:1fr 1fr 1fr;column-gap:32px}
.aiv-feat .r{padding:14px 0;border-top:1px solid var(--hair)}
.aiv-input{width:100%;background:#fff;border:1px solid var(--hair);border-radius:14px;color:var(--ink);font-size:14px;padding:12px 14px;resize:none;font-family:inherit}
.aiv-input::placeholder{color:#86868b}
.aiv-input:focus{outline:none;border-color:#0071e3;box-shadow:0 0 0 3px rgba(0,113,227,.25)}
.aiv-foot{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:22px 64px;border-top:1px solid #ededf0;font-size:11px;color:var(--ink-3)}
.aiv-foot img{height:17px;width:auto;opacity:.72}
.aiv-ask{display:flex;align-items:center;gap:10px;background:#fff;border:1px solid var(--hair);border-radius:980px;padding:8px 8px 8px 10px;box-shadow:0 1px 2px rgba(0,0,0,.04),0 8px 24px rgba(0,0,0,.06);transition:border-color .2s,box-shadow .2s}
.aiv-ask:focus-within{border-color:#0071e3;box-shadow:0 0 0 4px rgba(0,113,227,.15)}
.aiv-ask input{flex:1;min-width:0;border:0;outline:none;background:transparent;font:inherit;font-size:16px;color:var(--ink);padding:8px 4px}
.aiv-ask input:focus{box-shadow:none}
.aiv-ask input::placeholder{color:var(--ink-3)}
.aiv-send{width:40px;height:40px;border-radius:50%;background:var(--btn);color:#fff;display:flex;align-items:center;justify-content:center;flex:0 0 auto;transition:background .2s,opacity .2s}
.aiv-send:disabled{background:#d2d2d7}
.aiv-verdict{background:var(--tile);border-radius:22px;padding:22px 24px}
.aiv-chip{display:inline-flex;align-items:center;gap:4px;font-size:12px;background:#fff;border:1px solid var(--hair);border-radius:8px;padding:3px 8px;color:var(--ink-2)}
.aiv-chip:hover{border-color:#c7c7cc;color:var(--ink)}
.aiv-row{background:var(--tile)}
.aiv-answer{margin-top:10px;white-space:pre-wrap;font-size:13.5px;line-height:1.55;color:var(--ink-2);background:var(--tile);border-radius:16px;padding:16px 18px;max-height:360px;overflow:auto}
.aiv-upsell{margin-top:24px;padding-top:20px;border-top:1px solid var(--hair);display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:16px}
@media (max-width:1023px){
  .aiv-sec{padding:56px 28px}
  .aiv-h1{font-size:44px}
  .aiv-fact{border-left:0;padding:0} .aiv-h2{font-size:32px}
  .aiv-feat{grid-template-columns:1fr 1fr}
}
@media (max-width:639px){
  .aiv-sec{padding:44px 20px}
  .aiv-foot{padding:18px 20px}
  .aiv-h1{font-size:34px} .aiv-h2{font-size:28px} .aiv-lead{font-size:16px}
  .aiv-num{font-size:46px} .aiv-num.sm{font-size:34px}
  .aiv-feat{grid-template-columns:1fr}
  .aiv-stepno{margin-bottom:18px}
}
@media (prefers-reduced-motion:reduce){.aiv-in{animation:none!important}}
`;

const Logo = ({ k, className = 'w-4 h-4' }) => <img src={`/llm-${k}.svg`} alt="" className={className} />;

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    if (!('IntersectionObserver' in window)) { setSeen(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setSeen(true); }, { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
}

/* ---------- Live quick check ---------- */
const PROGRESS = [
  { at: 0, text: 'Asking ChatGPT' },
  { at: 6, text: 'Searching the web' },
  { at: 22, text: 'Reading the answer' },
  { at: 40, text: 'Almost there' },
];

function QuickResult({ check, brandName, onTrack }) {
  const [showAnswer, setShowAnswer] = useState(false);
  const r = check.result || {};
  const brands = r.brands || [];
  const t = r.target || {};
  const ranked = brands.filter((b) => b.rank != null);
  const mentionedOnly = brands.filter((b) => b.rank == null);

  return (
    <div className="aiv-in text-left mt-6">
      <div className="aiv-verdict" data-ok={t.mentioned ? '1' : '0'}>
        <div className="text-[12px] font-semibold" style={{ color: 'var(--ink-3)' }}>“{check.question}”</div>
        <div className="aiv-ttl mt-2" style={{ fontSize: 24 }}>
          {t.mentioned
            ? (t.rank ? <>{brandName} was recommended <span style={{ color: 'var(--brand)' }}>#{t.rank}</span>.</> : <>{brandName} was mentioned, but not recommended.</>)
            : <>{brandName} was <span style={{ color: '#d70015' }}>not mentioned</span>.</>}
        </div>
        <div className="aiv-txt">
          {t.mentioned && t.context ? t.context : (
            ranked.length
              ? <>ChatGPT recommended {ranked.length} other {ranked.length === 1 ? 'option' : 'options'} instead.</>
              : <>The answer did not recommend any specific brand.</>
          )}
        </div>
      </div>

      {brands.length > 0 && (
        <div className="mt-5">
          <div className="aiv-lbl"><span>Brands in the answer, in order</span><span>{r.searchCalls === 0 ? 'ChatGPT · answered without searching' : 'ChatGPT · web search'}</span></div>
          <div className="space-y-1.5">
            {ranked.map((b) => (
              <div key={b.name} className={`aiv-row ${b.own ? 'own' : ''}`}>
                <span className="k">{b.rank}</span>
                <span className="font-semibold whitespace-nowrap">{b.name}</span>
                <span className="w">{b.reason}</span>
              </div>
            ))}
            {mentionedOnly.length > 0 && (
              <div className="text-[12.5px] pt-1" style={{ color: 'var(--ink-3)' }}>
                Also mentioned: {mentionedOnly.map((b) => b.name).join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {(r.citations || []).length > 0 && (
        <div className="mt-5">
          <div className="aiv-lbl"><span>Sources ChatGPT read</span></div>
          <div className="aiv-src">
            {r.citations.map((c) => (
              <a key={c.url} href={c.url} target="_blank" rel="noreferrer" className="aiv-chip">{c.domain}<ExternalLink className="w-3 h-3" /></a>
            ))}
          </div>
        </div>
      )}

      {check.answer && (
        <div className="mt-5">
          <button onClick={() => setShowAnswer((v) => !v)} className="aiv-link inline-flex items-center gap-1">
            {showAnswer ? 'Hide' : 'Read'} the full answer <ChevronDown className={`w-4 h-4 transition-transform ${showAnswer ? 'rotate-180' : ''}`} />
          </button>
          {showAnswer && <div className="aiv-answer aiv-in">{check.answer}</div>}
        </div>
      )}

      <div className="aiv-upsell">
        <div>
          <div className="font-semibold" style={{ letterSpacing: '-.015em' }}>This is one answer, from one assistant, today.</div>
          <div className="aiv-txt" style={{ marginTop: 2 }}>Answers change from run to run. Tracking means many questions, three assistants, twice a week.</div>
        </div>
        <button onClick={onTrack} className="aiv-btn shrink-0">Track this for {brandName}</button>
      </div>
    </div>
  );
}

function QuickCheck({ onTrack }) {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const brandName = selectedBrand?.name || 'Your brand';
  const opts = useCallback((extra = {}) => ({ credentials: 'include', ...extra, headers: { 'X-Brand-ID': brandId, ...(extra.headers || {}) } }), [brandId]);

  const [question, setQuestion] = useState('');
  const [info, setInfo] = useState(null); // {remaining, limit}
  const [check, setCheck] = useState(null); // current/last result row
  const [running, setRunning] = useState(null); // {id, startedAt}
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  // Remaining checks + latest result
  useEffect(() => {
    if (!brandId) return;
    setCheck(null); setRunning(null); setError(null);
    fetch(`${API}/api/ai-visibility/quick-check`, opts())
      .then((r) => r.json())
      .then((j) => {
        setInfo({ remaining: j.remaining, limit: j.limit });
        const last = (j.recent || [])[0];
        if (last?.status === 'running') setRunning({ id: last.id, startedAt: new Date(last.createdAt).getTime() });
        else if (last?.status === 'done') setCheck(last);
      })
      .catch(() => {});
  }, [brandId, opts]);

  // Poll the running check
  useEffect(() => {
    if (!running) return undefined;
    const tick = setInterval(() => setElapsed(Math.round((Date.now() - running.startedAt) / 1000)), 1000);
    const poll = setInterval(async () => {
      try {
        const j = await fetch(`${API}/api/ai-visibility/quick-check/${running.id}`, opts()).then((r) => r.json());
        if (j.status === 'done') { setCheck(j); setRunning(null); }
        else if (j.status === 'error') { setError('The assistant did not answer this time. It did not count against your daily checks, please try again.'); setRunning(null); setInfo((i) => i && { ...i, remaining: i.remaining + 1 }); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [running, opts]);

  const submit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (q.length < 8 || running) return;
    setError(null); setCheck(null);
    try {
      const res = await fetch(`${API}/api/ai-visibility/quick-check`, opts({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q }) }));
      const j = await res.json();
      if (res.status === 409 && j.id) { setRunning({ id: j.id, startedAt: Date.now() }); return; }
      if (!res.ok) { setError(j.error || 'Something went wrong.'); if (res.status === 429) setInfo((i) => i && { ...i, remaining: 0 }); return; }
      setInfo((i) => ({ ...(i || { limit: 3 }), remaining: j.remaining }));
      setElapsed(0);
      setRunning({ id: j.id, startedAt: Date.now() });
    } catch { setError('Something went wrong. Please try again.'); }
  };

  const step = [...PROGRESS].reverse().find((p) => elapsed >= p.at) || PROGRESS[0];
  const out = info && info.remaining <= 0;

  return (
    <div className="mx-auto" style={{ maxWidth: 720 }}>
      <form onSubmit={submit} className="aiv-ask">
        <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0" style={{ boxShadow: '0 0 0 1px #e0e0e3' }}>
          <Logo k="chatgpt" className="w-4 h-4" />
        </span>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={300}
          disabled={!!running || out}
          placeholder={out ? 'You have used today’s free checks. Come back tomorrow.' : 'Ask what your customers would ask, in Turkish'}
        />
        <button type="submit" disabled={!!running || out || question.trim().length < 8} className="aiv-send" aria-label="Run check">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
        </button>
      </form>
      <div className="text-[12.5px] mt-3 text-center" style={{ color: 'var(--ink-3)' }}>
        e.g. “Yeni başlayanlar için hangi koşu ayakkabısını önerirsin?”
        {info && <> · {info.remaining} of {info.limit} free checks left today</>}
      </div>

      {running && (
        <div className="aiv-in mt-6 text-left aiv-verdict">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand)' }} />
            <div className="aiv-ttl" style={{ fontSize: 18 }}>{step.text}</div>
            <span className="ml-auto text-[13px]" style={{ color: 'var(--ink-3)' }}>{elapsed}s</span>
          </div>
          <div className="aiv-bar mt-4"><div className="own" style={{ width: `${Math.min(95, (elapsed / 50) * 100)}%`, transition: 'width 1s linear' }} /></div>
          <div className="aiv-txt">ChatGPT searches the web before answering, the same way it does for your customers. This usually takes 20 to 60 seconds.</div>
        </div>
      )}
      {error && <div className="aiv-in mt-4 text-[14px] text-center" style={{ color: '#d70015' }}>{error}</div>}
      {check && !running && <QuickResult check={check} brandName={brandName} onTrack={onTrack} />}
    </div>
  );
}

/* ---------- Dashboard preview ---------- */
function PanelPreview() {
  const [ref, on] = useInView();
  const max = SAMPLE_SHARE[0].share;
  return (
    <div ref={ref} className="aiv-grid grid-cols-1 lg:grid-cols-3">
      <div className="aiv-tile">
        <div className="aiv-lbl"><span>Share of recommendation</span><span>Example</span></div>
        <div className="space-y-3">
          {SAMPLE_SHARE.map((b, i) => (
            <div key={b.name}>
              <div className="flex justify-between text-[13px] mb-1.5">
                <span style={{ color: b.own ? 'var(--brand)' : 'var(--ink-2)', fontWeight: b.own ? 600 : 500 }}>{b.name}</span>
                <span style={{ color: b.own ? 'var(--brand)' : 'var(--ink-3)', fontWeight: b.own ? 600 : 500 }}>{b.share}%</span>
              </div>
              <div className="aiv-bar">
                <div className={b.own ? 'own' : ''} style={{ width: on ? `${(b.share / max) * 100}%` : '0%', transition: `width .9s cubic-bezier(.2,.8,.2,1) ${i * 80}ms` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="aiv-tile">
        <div className="aiv-lbl"><span>Need map</span><span>Example</span></div>
        <div className="grid items-center gap-1.5 text-[12.5px]" style={{ gridTemplateColumns: '1fr repeat(4, 2.4rem)' }}>
          <span />
          {HEAT_BRANDS.map((b) => <span key={b} className="text-center font-semibold" style={{ color: b === 'You' ? 'var(--brand)' : 'var(--ink-3)' }}>{b}</span>)}
          {HEAT_ROWS.map((r, ri) => (
            <React.Fragment key={r.need}>
              <span className="truncate" style={{ color: 'var(--ink-2)' }}>{r.need}</span>
              {r.v.map((v, ci) => (
                <span key={ci} className="h-9 rounded-lg"
                  style={{ background: `rgba(10,91,211,${on ? 0.06 + v * 0.8 : 0.04})`, transition: `background .6s ease ${(ri * 4 + ci) * 40}ms` }} />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="aiv-tile">
        <div className="aiv-lbl"><span>Where answers come from</span><span>Example</span></div>
        <div className="space-y-2.5">
          {SOURCE_MIX.map((s, i) => (
            <div key={s.label} className="flex items-center gap-3 text-[13px]">
              <span className="w-28 shrink-0" style={{ color: 'var(--ink-2)' }}>{s.label}</span>
              <div className="aiv-bar flex-1" style={{ height: 18, borderRadius: 6 }}>
                <div style={{ width: on ? `${(s.pct / SOURCE_MIX[0].pct) * 100}%` : '0%', borderRadius: 6, background: i === 0 ? 'var(--brand)' : '#b9b9c0', transition: `width .9s cubic-bezier(.2,.8,.2,1) ${i * 80}ms` }} />
              </div>
              <span className="w-9 text-right" style={{ color: 'var(--ink-3)' }}>{s.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Contact ---------- */
function ContactForm() {
  const { user, selectedBrand } = useAuth();
  const [note, setNote] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: user?.name || user?.email || 'Panel user',
          email: user?.email,
          company: selectedBrand?.name,
          message: `[AI Visibility request]\nBrand: ${selectedBrand?.name || '-'} (${selectedBrand?.id || '-'})\n\n${note.trim() || '(no note)'}`,
          marketingConsent: false,
        }),
      });
      if (!res.ok) throw new Error();
      setState('sent');
    } catch {
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className="aiv-tile aiv-in" style={{ flexDirection: 'row', gap: 14, alignItems: 'flex-start', background: '#fff' }}>
        <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: '#28b463' }} />
        <div>
          <div className="aiv-ttl">We got your request.</div>
          <p className="aiv-cap">{user?.email ? <>We will get back to you at <b style={{ color: 'var(--ink)' }}>{user.email}</b>.</> : 'We will get back to you shortly.'}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="aiv-input"
        placeholder="Add a note if you like. Competitors you want tracked, priority categories." />
      <div className="flex flex-wrap items-center justify-center gap-4">
        <button type="submit" disabled={state === 'sending' || !user?.email} className="aiv-btn">
          {state === 'sending' && <Loader2 className="w-4 h-4 animate-spin" />}
          Get in touch
        </button>
        <span className="text-[13px]" style={{ color: 'var(--ink-3)' }}>
          {state === 'error'
            ? <span style={{ color: '#d70015' }}>Could not send. Email us at <a href="mailto:efe@glowytics.com?subject=AI%20Visibility" className="underline">efe@glowytics.com</a>.</span>
            : <>We will reply to <span style={{ color: 'var(--ink)' }}>{user?.email}</span></>}
        </span>
      </div>
    </form>
  );
}

export default function AiVisibilityLanding() {
  const { selectedBrand } = useAuth();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="aiv min-h-screen" lang="en">
      <style>{CSS}</style>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="aiv-page">

          {/* Hero: live check */}
          <section className="aiv-sec aiv-center" style={{ paddingBottom: 72 }}>
            <div className="aiv-eyebrow">AI Visibility</div>
            <h1 className="aiv-h1">Is AI recommending<br />your brand?</h1>
            <p className="aiv-lead">
              <b>Your customers now ask ChatGPT, Gemini and Claude what to buy.</b> Ask one of their questions below
              and see, right now, which brands ChatGPT recommends.
            </p>
            <div className="mt-10"><QuickCheck onTrack={() => go('aiv-contact')} /></div>
            <div className="mt-8">
              <button onClick={() => go('aiv-how')} className="aiv-link">How tracking works ›</button>
            </div>
          </section>

          {/* Why */}
          <section className="aiv-sec">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-end">
              <div>
                <div className="aiv-eyebrow">Why it matters</div>
                <h2 className="aiv-h2">One answer instead<br />of ten links.</h2>
              </div>
              <p className="aiv-lead" style={{ marginTop: 0 }}>
                Ranking on the first page of search used to be enough. An AI assistant names a handful of brands and moves on. <b>If you are not on that list, the customer never sees you.</b>
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 mt-14">
              <div className="aiv-fact">
                <div className="aiv-num" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>3
                  <span className="flex -space-x-1.5">
                    {['chatgpt', 'gemini', 'claude'].map((k) => (
                      <span key={k} className="w-7 h-7 rounded-full bg-white flex items-center justify-center" style={{ boxShadow: '0 0 0 1px #e0e0e3' }}><Logo k={k} className="w-4 h-4" /></span>
                    ))}
                  </span>
                </div>
                <div className="aiv-cap">assistants, with web search on and a Turkish location.</div>
              </div>
              <div className="aiv-fact">
                <div className="aiv-num">2<span style={{ fontSize: '.42em', color: 'var(--ink-3)', marginLeft: 8, letterSpacing: '-.01em' }}>/ week</span></div>
                <div className="aiv-cap">measurements. The same questions are asked again and again.</div>
              </div>
              <div className="aiv-fact">
                <div className="aiv-num" style={{ color: 'var(--brand)' }}>95%</div>
                <div className="aiv-cap">confidence intervals separate real change from model noise.</div>
              </div>
              <div className="aiv-fact">
                <div className="aiv-num">Raw</div>
                <div className="aiv-cap">answers are all stored, so past months can be recalculated.</div>
              </div>
            </div>
          </section>

          {/* How it works */}
          <section id="aiv-how" className="aiv-sec">
            <div className="aiv-eyebrow">How it works</div>
            <h2 className="aiv-h2">Set up once.<br />Measured every week.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
              {STEPS.map((s, i) => (
                <div key={s.title} style={{ borderTop: '1px solid var(--hair)', paddingTop: 18 }}>
                  <div className="aiv-stepno">0{i + 1}</div>
                  <div className="aiv-ttl">{s.title}</div>
                  <div className="aiv-txt">{s.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Dashboard */}
          <section className="aiv-sec">
            <div className="aiv-eyebrow">Dashboard</div>
            <h2 className="aiv-h2">See where competitors win,<br />and why.</h2>
            <p className="aiv-lead">Once it is set up for your brand, this page fills with your own data. Below is an example.</p>
            <div className="mt-10"><PanelPreview /></div>
            <div className="aiv-feat mt-10">
              {FEATURES.map((f) => (
                <div key={f.title} className="r">
                  <div className="text-[15px] font-semibold" style={{ letterSpacing: '-.015em' }}>{f.title}</div>
                  <div className="aiv-txt" style={{ marginTop: 3 }}>{f.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="aiv-contact" className="aiv-sec aiv-center" style={{ background: 'var(--tile)', maxWidth: 'none' }}>
            <div className="mx-auto" style={{ maxWidth: 620 }}>
              <h2 className="aiv-h2">{selectedBrand?.name ? <>Let’s set it up<br />for {selectedBrand.name}.</> : <>Let’s set it up<br />for your brand.</>}</h2>
              <p className="aiv-lead">
                We agree on your category, the competitors to track and the question list together. If you already have a list of questions, we can use it.
              </p>
              <div className="mt-8 text-left"><ContactForm /></div>
            </div>
          </section>

          <div className="aiv-foot">
            <span>AI Visibility</span>
            <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" />
          </div>
        </div>
      </div>
    </div>
  );
}
